import React, { useState } from 'react';
import { CameraIcon, CheckIcon, XIcon } from 'lucide-react';
import { AppShell } from '../../../components/layout/AppShell';
import { SubNav, accountNav } from '../../../components/layout/SubNav';
import { Card } from '../../../components/ui/Panels';
import { Button } from '../../../components/ui/Button';
import { Field, Input, Select, Switch, Textarea } from '../../../components/ui/Field';
import { customer } from '../../../data/customer';
import type { HobType, KitchenEquipment, SpaceRating } from '../../../types/customer';
const hobs: {
  id: HobType;
  label: string;
}[] = [{
  id: 'gas',
  label: 'Gas'
}, {
  id: 'induction',
  label: 'Induction'
}, {
  id: 'electric',
  label: 'Electric coil'
}, {
  id: 'ceramic',
  label: 'Ceramic'
}];
const ratings: SpaceRating[] = ['tight', 'workable', 'plenty'];
export function Kitchen() {
  const [hob, setHob] = useState<HobType>(customer.kitchen.hob);
  const [burners, setBurners] = useState(customer.kitchen.burners);
  const [oven, setOven] = useState(customer.kitchen.oven);
  const [fridge, setFridge] = useState<SpaceRating>(customer.kitchen.fridgeSpace);
  const [worktop, setWorktop] = useState<SpaceRating>(customer.kitchen.worktop);
  const [dishwasher, setDishwasher] = useState(customer.kitchen.dishwasher);
  const [equipment, setEquipment] = useState<KitchenEquipment[]>(customer.kitchen.equipment);
  const [notes, setNotes] = useState(customer.kitchen.notes);
  const [access, setAccess] = useState(customer.kitchen.access);
  const [parking, setParking] = useState(customer.kitchen.parking);
  const have = equipment.filter(e => e.available).length;
  const toggle = (id: string) => setEquipment(equipment.map(e => e.id === id ? {
    ...e,
    available: !e.available
  } : e));
  return <AppShell title="Kitchen profile" subtitle="What a chef can count on when they arrive" data-id="element-1547" data-mp="3jna9c0kqhs6s">
      <SubNav items={accountNav} data-id="element-1548" data-mp="3floont1yk9m1" />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1549" data-mp="3flf62qj8t4qd">
        <div className="space-y-6" data-id="element-1550" data-mp="3h33ugxul0hbg">
          <Card className="p-5" data-id="element-1551" data-mp="3h45i01ssawa2">
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1552" data-mp="3h0rh6xzrqv4q">The basics</h2>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1553" data-mp="3m15lq625vmy0">
              Wok work is the usual sticking point in Bangkok condos — burner type decides which menus are realistic.
            </p>

            <div className="mt-4" data-id="element-1554" data-mp="3c26e8qt4txcl">
              <p className="mb-1.5 text-[13px] font-semibold text-ink" data-id="element-1555" data-mp="2yccom69sz91t">Hob</p>
              <div className="flex flex-wrap gap-1.5" data-id="element-1556" data-mp="2ya9o2sb0xxiu">
                {hobs.map(option => <button key={option.id} type="button" aria-pressed={hob === option.id} onClick={() => setHob(option.id)} className={`rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${hob === option.id ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink'}`} data-id="element-1557" data-mp="334dzpov8itq9">
                    {option.label}
                  </button>)}
              </div>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2" data-id="element-1558" data-mp="29r7ys3oclqmz">
              <Field label="Burners" id="k-burners" data-id="element-1559" data-mp="29srgzztkebre">
                <Input id="k-burners" type="number" min={1} max={8} value={burners} onChange={e => setBurners(Number(e.target.value) || 1)} className="tnum" data-id="element-1560" data-mp="24rv9zddx29hr" />
              </Field>
              <Field label="Oven" id="k-oven" data-id="element-1561" data-mp="2sgo1mxni9v6k">
                <Input id="k-oven" value={oven} onChange={e => setOven(e.target.value)} data-id="element-1562" data-mp="2ogcgzkv57uv3" />
              </Field>
              <Field label="Fridge space" id="k-fridge" data-id="element-1563" data-mp="32fvn684e8w4r">
                <Select id="k-fridge" value={fridge} onChange={e => setFridge(e.target.value as SpaceRating)} data-id="element-1564" data-mp="32geuyw0by452">
                  {ratings.map(r => <option key={r} value={r} data-id="element-1565" data-mp="3hjyhfe9ftziy">
                      {r}
                    </option>)}
                </Select>
              </Field>
              <Field label="Worktop space" id="k-worktop" data-id="element-1566" data-mp="3qbgw1kqqk79x">
                <Select id="k-worktop" value={worktop} onChange={e => setWorktop(e.target.value as SpaceRating)} data-id="element-1567" data-mp="3qc0fj5w1b3q2">
                  {ratings.map(r => <option key={r} value={r} data-id="element-1568" data-mp="2yksva9sz53ws">
                      {r}
                    </option>)}
                </Select>
              </Field>
            </div>

            <div className="mt-2 border-t border-hairline" data-id="element-1569" data-mp="37kz8rvooa3vy">
              <Switch checked={dishwasher} onChange={setDishwasher} label="Dishwasher" description="Affects how long clear-down takes." data-id="element-1570" data-mp="37jo29o3uei15" />
            </div>
          </Card>

          <Card className="p-5" data-id="element-1571" data-mp="mtoy7ppx4t3t">
            <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-1572" data-mp="mui4kpkytglf">
              <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1573" data-mp="rs9xnmxcj8r7">Equipment</h2>
              <span className="tnum text-[13px] text-ink-2" data-id="element-1574" data-mp="2cakh7gmr70tf">
                {have} of {equipment.length} available
              </span>
            </div>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1575" data-mp="22m2kpblibfvl">
              Chefs see this when quoting. Anything missing they can bring — but only if they know in advance.
            </p>
            <ul className="mt-4 grid gap-1.5 sm:grid-cols-2" data-id="element-1576" data-mp="1xkwqf9c77kjy">
              {equipment.map(item => <li key={item.id} data-id="element-1577" data-mp="1tj1g5fcahwu1">
                  <button type="button" aria-pressed={item.available} onClick={() => toggle(item.id)} className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors duration-150 ease-out ${item.available ? 'border-primary/40 bg-primary-soft' : 'border-hairline'}`} data-id="element-1578" data-mp="1om1kzzgzaehp">
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded ${item.available ? 'bg-primary text-on-primary' : 'border border-hairline text-transparent'}`} aria-hidden="true" data-id="element-1579" data-mp="3901vul3prx79">
                      {item.available ? <CheckIcon className="h-3 w-3" data-id="element-1580" data-mp="12dcnyz48mqh6" /> : <XIcon className="h-3 w-3" data-id="element-1581" data-mp="12efc4za6eete" />}
                    </span>
                    <span className={`text-[13px] ${item.available ? 'font-medium text-ink' : 'text-ink-3'}`} data-id="element-1582" data-mp="1q4i1v8qmb8ou">{item.label}</span>
                  </button>
                </li>)}
            </ul>
          </Card>

          <Card className="p-5" data-id="element-1583" data-mp="1h28qyhrftdax">
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1584" data-mp="1l4djfd65g3vz">Getting in</h2>
            <div className="mt-4 space-y-4" data-id="element-1585" data-mp="1l6gggh5pyxa7">
              <Field label="Access" id="k-access" data-id="element-1586" data-mp="5nddgz4j1hvy">
                <Input id="k-access" value={access} onChange={e => setAccess(e.target.value)} data-id="element-1587" data-mp="5pprj175z83q" />
              </Field>
              <Field label="Parking" id="k-parking" data-id="element-1588" data-mp="25pgyr8k1xh3">
                <Input id="k-parking" value={parking} onChange={e => setParking(e.target.value)} data-id="element-1589" data-mp="244hqobapqcw" />
              </Field>
              <Field label="House rules and quirks" id="k-notes" hint="extractor, balcony rules, pets" data-id="element-1590" data-mp="3te37n6at2pcm">
                <Textarea id="k-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)} data-id="element-1591" data-mp="c1j04tgs4p7u" />
              </Field>
            </div>
            <Button type="button" variant="action" className="mt-5" data-id="element-1592" data-mp="3fjsnl29r7jcw">
              Save kitchen profile
            </Button>
          </Card>
        </div>

        <aside className="space-y-4" data-id="element-1593" data-mp="3ajo9tk08d2qd">
          <Card className="p-5" data-id="element-1594" data-mp="3panc6dah2v1i">
            <div className="flex items-center gap-2" data-id="element-1595" data-mp="3pbyjuo71gmxw">
              <CameraIcon className="h-4 w-4 text-ink-2" aria-hidden="true" data-id="element-1596" data-mp="3kj5b7p8g8ugj" />
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1597" data-mp="2wu49sly1ajkr">Photos</h2>
            </div>
            <ul className="mt-3 space-y-3" data-id="element-1598" data-mp="2rvj6ueredlsm">
              {customer.kitchen.photos.map(photo => <li key={photo.id} data-id="element-1599" data-mp="26cht552yz3pd">
                  <img src={photo.url} alt={photo.caption} className="aspect-[4/3] w-full rounded-lg border border-hairline object-cover" loading="lazy" data-id="element-1600" data-mp="26dj479jzjq73" />
                  <p className="mt-1.5 text-[12px] text-ink-3" data-id="element-1601" data-mp="2kbzbb1lrfipr">{photo.caption}</p>
                </li>)}
            </ul>
            <Button type="button" variant="outline" size="sm" full className="mt-3" data-id="element-1602" data-mp="2p9r9gdd0012e">
              Add a photo
            </Button>
          </Card>

          <Card className="p-5" data-id="element-1603" data-mp="2z0dpu9sjgvm5">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1604" data-mp="2z1ezqaxtjc2a">Serving ware</h2>
            <p className="tnum mt-1 font-display text-2xl font-semibold text-ink" data-id="element-1605" data-mp="33y5f0w1su3mn">{customer.kitchen.serviceWareFor}</p>
            <p className="text-[13px] text-ink-2" data-id="element-1606" data-mp="382v7sc15v23c">place settings you can lay</p>
            <p className="mt-3 text-[12px] text-ink-3" data-id="element-1607" data-mp="3d0wst2i5dbhg">
              Chefs bring their own for larger tables, usually as a flat add-on.
            </p>
          </Card>
        </aside>
      </div>
    </AppShell>;
}